import * as Radix from "@radix-ui/themes";
import * as React from "react";
import {
  MarketplaceContexts,
  Props as MarketplaceContextsProps,
} from "~/components/MarketplaceContexts";
import * as Analytics from "~/contexts/analyticsContext";
import {
  ConsentState,
  CookiePreferences,
  getStoredPreferences,
} from "~/contexts/cookieConsentContext";
import * as ReactRouterStubs from "~/contexts/reactRouterStubsContext";
import { useInitDialog } from "~/hooks/useDialog";
import { useInitDrawer } from "~/hooks/useDrawer";
import styles from "~/root.module.css";
import * as ApiIdentitySchema from "~/routes/_marketplace.api.identity.v2/schema";

export type Props = {
  styleSheetUrl: string;
  auth: ApiIdentitySchema.Response;
  segmentWriteKey?: string;
  reforgeApiKey?: string;
  clerkPublishableKey?: string | null;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  children?: React.ReactNode;
};

type ConsentUpdateDetail = {
  state: ConsentState;
  preferences: CookiePreferences;
};

// Renders components outside of remix (ie WebFlow).
export const External: React.FC<Props> = (props) => {
  const reactRouterStubs = ReactRouterStubs.useVanillaJsReactRouterStubs();

  const cookiestring = RegExp("ajs_anonymous_id=[^;]+").exec(document.cookie);
  const cookieAnonymousId = decodeURIComponent(
    cookiestring ? cookiestring.toString().replace(/^[^=]+./, "") : "",
  );

  const auth = React.useMemo<ApiIdentitySchema.Response>(
    () => ({
      ...props.auth,
      anonymousId: props.auth.anonymousId || cookieAnonymousId,
    }),
    [props.auth, cookieAnonymousId],
  );

  const authentication: MarketplaceContextsProps["authentication"] =
    React.useMemo(
      () => ({
        isVendrInternalEmployee: auth.isVendrInternalEmployee,
        isSyntheticTest: auth.isSyntheticTest,
        identity: auth.identity ?? null,
        identityV2: auth.identityV2 ?? null,
        platformOptedInCookie: auth.platformOptedInCookie ?? null,
        isAuthenticatedWithVendrPlatform: auth.isAuthenticatedWithVendrPlatform,
      }),
      [
        auth.isSyntheticTest,
        auth.isVendrInternalEmployee,
        auth.identity,
        auth.identityV2,
        auth.platformOptedInCookie,
        auth.isAuthenticatedWithVendrPlatform,
      ],
    );

  // Track whether analytics has been initialized using a ref to avoid re-renders
  const analyticsLoadedRef = React.useRef(false);

  // Initial analytics load: check localStorage once on mount
  React.useLayoutEffect(() => {
    // Read preferences synchronously on mount to catch any pre-existing consent
    const preferences = getStoredPreferences();

    // Only load analytics if consent has been given and not already loaded
    if (
      preferences.analytics &&
      props.segmentWriteKey &&
      !analyticsLoadedRef.current
    ) {
      Analytics.analytics.load({ writeKey: props.segmentWriteKey });
      analyticsLoadedRef.current = true;
    }
  }, [props.segmentWriteKey]);

  // Event listener for consent changes: separate effect to avoid re-registration
  React.useLayoutEffect(() => {
    const handleConsentUpdate = (event: CustomEvent<ConsentUpdateDetail>) => {
      const { preferences: updatedPreferences } = event.detail;

      // Load analytics if consent is granted and not already loaded
      if (
        updatedPreferences.analytics &&
        props.segmentWriteKey &&
        !analyticsLoadedRef.current
      ) {
        Analytics.analytics.load({ writeKey: props.segmentWriteKey });
        analyticsLoadedRef.current = true;
      }
    };

    window.addEventListener(
      "vendrConsentUpdate",
      handleConsentUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        "vendrConsentUpdate",
        handleConsentUpdate as EventListener,
      );
    };
  }, [props.segmentWriteKey]);

  const analytics: MarketplaceContextsProps["analytics"] = React.useMemo(
    () => ({
      anonymousId: auth.anonymousId,
      analytics: Analytics.analytics,
    }),
    [auth.anonymousId],
  );

  const searchInputRef = React.useRef<HTMLDivElement | null>(null);
  const search: MarketplaceContextsProps["search"] = React.useMemo(
    () => ({
      appId: props.algolia?.appId ?? "",
      defaultIndex: props.algolia?.defaultIndex ?? "",
      productTierIndex: props.algolia?.productTierIndex ?? "",
      searchKey: props.algolia?.searchKey ?? "",
      searchInputRef: searchInputRef,
    }),
    [
      props.algolia?.appId,
      props.algolia?.defaultIndex,
      props.algolia?.searchKey,
      props.algolia?.productTierIndex,
    ],
  );

  const dialog = useInitDialog();

  const portalContainer = React.useRef<null | HTMLDivElement>(null);
  const portal: MarketplaceContextsProps["portal"] = React.useMemo(
    () => ({
      container: portalContainer,
    }),
    [],
  );

  const drawer = useInitDrawer();

  return (
    <Radix.Theme
      radius="small"
      accentColor="violet"
      grayColor="gray"
      className="frontend-ts-themes global-vars"
    >
      <MarketplaceContexts
        clerkPublishableKey={props.clerkPublishableKey}
        reforgeApiKey={props.reforgeApiKey}
        authentication={authentication}
        reactRouterStubs={reactRouterStubs}
        analytics={analytics}
        requestHeaders={null}
        search={search}
        dialog={dialog}
        portal={portal}
        drawer={drawer}
      >
        {props.children}
      </MarketplaceContexts>
      <div
        ref={dialog.container}
        className={styles.fullPageDialogContainer}
      ></div>
      <div ref={portalContainer}></div>
      <div ref={drawer.container}></div>
    </Radix.Theme>
  );
};
